import {
  DashboardEventInterface,
  DashboardInterface,
  EventInterface,
  EventListInterface,
  EventListItemInterface,
} from '@elearning-platform/api-types/interfaces';
import { Color, IconName, useNotifications, UseNotifications } from '@elearning-platform/ui';
import { QueryClient, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { TransProps, useTranslation } from 'react-i18next';

import { disenrollEvent } from '../api/requests';
import { QueryKey } from '../enums';

export const useEventDisenroll: () => UseMutationResult<void, Error, number> = (): UseMutationResult<void, Error, number> => {
  const { addToast, showPopup, hidePopup }: UseNotifications = useNotifications();
  const { t }: TransProps<never> = useTranslation();
  const queryClient: QueryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (eventId: number): Promise<void> => disenrollEvent(eventId),
    onSuccess: (_: void, eventId: number): void => {
      addToast({
        text: t('competence.app.trainingsV2.disenrollSuccessMessage'),
        icon: IconName.CheckmarkCircle,
        iconColor: Color.Green900,
      });
      queryClient.removeQueries({ queryKey: [QueryKey.EnrollEventSurveyData] });
      queryClient.setQueryData(
        [QueryKey.Events],
        (old: EventListInterface | undefined): EventListInterface | undefined => old && ({
          ...old,
          elements: old.elements.map((element: EventListItemInterface): EventListItemInterface =>
            element.id === eventId
              ? {
                  ...element,
                  userFlags: { ...element.userFlags, isEnrolled: false, canBeEnrolled: true, canBeDisenrolled: false },
                  entrySurvey: element.entrySurvey ? { ...element.entrySurvey, attemptId: null } : null,
                }
              : element
          ),
        })
      );
      queryClient.setQueryData(
        [QueryKey.Event, eventId.toString()],
        (old: EventInterface | undefined): EventInterface | undefined => old && ({
          ...old,
          userFlags: { ...old.userFlags, isEnrolled: false, canBeEnrolled: true, canBeDisenrolled: false },
          entrySurvey: old.entrySurvey ? { ...old.entrySurvey, attemptId: null } : null,
        })
      );
      queryClient.setQueryData(
        [QueryKey.DashboardData],
        (old: DashboardInterface | undefined): DashboardInterface | undefined => old && ({
          ...old,
          events: {
            ...old.events,
            recommended: old.events.recommended.map((item: DashboardEventInterface): DashboardEventInterface =>
              item.id === eventId
                ? { ...item, userFlags: { ...item.userFlags, isEnrolled: false, canBeEnrolled: true, canBeDisenrolled: false } }
                : item
            ),
          },
        })
      );
    },
    onError: (error: Error): void => showPopup({
      title: t('competence.app.global.errorOccurred'),
      description: error.message || t('competence.app.global.defaultErrorDescription'),
      acceptButtonData: {
        label: t('competence.app.global.close'),
        onClick: hidePopup,
      },
    }),
  });
};
